'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogAppCTA, BlogWhatTheAppChecks, BlogSources, BlogTLDR, BlogKeyTakeaways, BlogFAQ } from '@/app/components/blog/BlogAeo'
import AppStoreLink from '@/app/components/AppStoreLink'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics and Children: Why Kids Face the Greatest Risk"
      author="MicroPlastics Team"
      date="October 15, 2025"
      readTime="10 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="MicroPlastics app helping check product safety"
    >
      <BlogTLDR>
        Children ingest <strong>significantly more microplastics per pound
        of body weight than adults</strong>. Infant feces contain ~10× more
        microplastics than adult feces (2021 University of New York study).
        The drivers: plastic baby bottles, formula prep with plastic, baby
        food pouches, plastic toys, indoor air from carpets and synthetic
        textiles, and packaged kids&rsquo; snacks. The swaps that help most target
        feeding and floor-level air quality.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Infant feces contain ~10× more microplastics per gram than adult feces (Zhang et al., 2021).</>,
          <>Plastic baby bottles release 1-16 million particles per liter (2020 Nature Food).</>,
          <>Children breathe closer to floor-level dust, where heavy synthetic-fibre concentrations sit.</>,
          <>Per pound of body weight, kids consume more food, more water, and breathe more air, exposure scales accordingly.</>,
          <>Top 5 swaps: glass/silicone baby bottles, glass-jar baby food, stainless lunchbox, no synthetic carpet in nursery, HEPA air filter.</>,
          <>2026: car cabins measured <strong>2,238 particles/m³</strong> vs <strong>528/m³</strong> in homes, so ventilating the car matters more than swapping plastic plates.</>,
        ]}
      />

      {/* Introduction */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Children Are Bearing the Heaviest Microplastic Burden
      </h2>
      <p>
        Of all the populations affected by microplastic pollution, children are the most vulnerable.
        This is not speculation -- it is a conclusion supported by a growing body of peer-reviewed
        research. Studies have consistently shown that infants and young children have{' '}
        <strong>higher concentrations of microplastics in their bodies</strong> than adults, and their
        developing organs and systems are more susceptible to the harm these particles can cause.
      </p>
      <p>
        A landmark 2021 study published in <em>Environmental Science &amp; Technology Letters</em>{' '}
        found that infant stool contained <strong>10 to 20 times more PET microplastic particles</strong>{' '}
        than adult stool. The researchers analyzed meconium (a newborn&apos;s first stool) and stool
        samples from one-year-old infants and adults, finding dramatically elevated levels in the
        youngest subjects. This means that from the very first days of life, babies are already
        carrying a disproportionate microplastic burden.
      </p>
      <p>
        Understanding why children face greater risk -- and what parents can do about it -- is
        essential for any family trying to navigate the growing microplastic crisis. This guide
        covers the science behind children&apos;s vulnerability, the key exposure sources at every
        age, and practical strategies parents can implement today.
      </p>

      {/* Why Children Are More Vulnerable */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        2026: Exposure Begins in the Womb, and Indoor Air Is the Main Channel
      </h2>
      <p>
        Two 2026 measurements sharpen the picture of childhood exposure, and both point
        earlier and more environmental than most guidance assumes.
      </p>
      <p>
        First, exposure precedes birth. A study in{' '}
        <em>Ecotoxicology and Environmental Safety</em> covering{' '}
        <strong>1,750 mother-infant pairs</strong> found higher placental microplastic content
        associated with <strong>lower birth weight, birth length and head circumference</strong>{' '}
        -- a 107.7 g birth-weight difference for total microplastics. Polyethylene was found in
        98.4% of samples. Separate work detected microplastics in fetal cerebrum, heart, lung,
        liver, kidney and intestine at roughly 22 weeks gestation.
      </p>
      <p>
        Second, the dominant childhood channel is probably <strong>air, not food</strong>.
        Indoor measurements published in <em>PLOS One</em>, counting only the lung-penetrating
        1--10 µm fraction, found a median of <strong>528 particles per cubic metre</strong> in
        homes and <strong>2,238 per cubic metre in car cabins</strong> -- over four times
        higher. Children spend a great deal of time in both, breathe faster relative to body
        mass than adults, and play at floor level where settled fibres are disturbed.
      </p>
      <p>
        That reorders the practical advice. Swapping plastic plates is visible and easy;{' '}
        <strong>HEPA filtration, hard floors, natural-fibre textiles, ventilating the car</strong>{' '}
        before a long drive, and regular sealed-system vacuuming are less visible and probably
        matter more.
      </p>
      <p>
        The standing caveat applies with extra force where children are concerned: these are
        observational associations, causation is not established, and none of this warrants
        anxiety about a child who uses plastic cups. It warrants the cheap environmental
        changes, which are worth making for air quality regardless.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why Children Are More Vulnerable Than Adults
      </h2>
      <p>
        Children&apos;s heightened vulnerability to microplastics is not a single factor but a
        convergence of biological, behavioral, and environmental factors that all point in the same
        direction: greater exposure and greater susceptibility to harm.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Higher Intake Per Body Weight
      </h3>
      <p>
        Children eat and drink more relative to their body weight than adults do. A toddler consuming
        the same amount of microplastic-contaminated food as an adult is receiving a proportionally
        much larger dose. For a 12-kilogram toddler eating foods with similar microplastic levels
        as a 70-kilogram adult, the per-kilogram exposure is roughly <strong>six times higher</strong>.
        This principle is well established in toxicology and applies to all environmental contaminants,
        not just microplastics.
      </p>
      <p>
        Children also breathe faster than adults, taking in more air per kilogram of body weight.
        Since airborne microplastic fibers are a significant exposure route, children inhale
        proportionally more microplastic particles than adults in the same environment.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Developing Organs and Systems
      </h3>
      <p>
        A child&apos;s organs are still growing and differentiating, making them more susceptible to
        disruption by foreign particles and chemicals. The endocrine system, which regulates hormones
        critical for growth and development, is particularly vulnerable. Many chemicals associated
        with microplastics -- including phthalates, BPA, and flame retardants -- are known endocrine
        disruptors. Exposure during critical developmental windows can have effects that do not
        manifest until years or even decades later.
      </p>
      <p>
        The gut barrier in infants and young children is also more permeable than in adults, meaning
        that a higher percentage of ingested microplastic particles may cross into the bloodstream.
        The blood-brain barrier, which protects the brain from foreign substances, is not fully
        developed until several years after birth, potentially allowing nanoplastic particles greater
        access to developing brain tissue.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Hand-to-Mouth Behavior
      </h3>
      <p>
        Young children explore the world by putting things in their mouths. This natural developmental
        behavior means they are constantly ingesting dust, fibers, and particles from their
        environment. Household dust has been found to contain significant concentrations of
        microplastic fibers, primarily from synthetic textiles and household furnishings. Studies
        estimate that a crawling infant may ingest up to <strong>100,000 microplastic fibers per
        day</strong> from floor dust alone -- a number that dwarfs dietary exposure.
      </p>
      <p>
        Plastic toys, teething rings, sippy cup lids, and pacifiers all provide additional direct
        oral contact with plastic materials. While these products are designed to be safe, they can
        release microplastic particles through wear, chewing, and degradation over time.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Longer Lifetime of Accumulation
      </h3>
      <p>
        A child born today will live their entire life in a world saturated with microplastics.
        The cumulative body burden they accumulate over 70 or 80 years will be unprecedented in
        human history. Even if individual daily doses seem small, the lifetime accumulation in
        tissues that do not efficiently clear particles -- such as the lungs, liver, and brain --
        could be substantial. Children starting with higher baseline exposure levels face the
        prospect of reaching even higher cumulative levels than previous generations.
      </p>

      {/* Studies on Children */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the Research Shows: Children Have Higher Microplastic Levels
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        The Infant Stool Study
      </h3>
      <p>
        The 2021 study from New York University that found 10 to 20 times more PET microplastics in
        infant stool compared to adults was a wake-up call for the scientific community. The
        researchers suggested several factors driving this disparity: infants&apos; extensive contact
        with plastic products (bottles, toys, mats), their crawling on plastic-fiber-shedding carpets,
        and their tendency to mouth plastic objects. The study detected polyethylene terephthalate (PET)
        and polycarbonate (PC) as the most common polymers, both widely used in baby products.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        BPA in Children: The NYU Study
      </h3>
      <p>
        A comprehensive study led by researchers at New York University&apos;s Grossman School of
        Medicine examined BPA levels in children and found that <strong>over 97% of children tested
        had detectable BPA in their urine</strong>. BPA, or bisphenol A, is a chemical building block
        of polycarbonate plastics and epoxy resins found in food containers, water bottles, and can
        linings. While BPA is technically a chemical rather than a microplastic, it is released from
        plastic products and is one of the primary toxicological concerns associated with microplastic
        exposure.
      </p>
      <p>
        The study found that children&apos;s BPA levels were significantly associated with behavioral
        changes, obesity risk, and markers of metabolic disruption. Children in the highest quartile
        of BPA exposure had a <strong>two-fold increased risk of obesity</strong> compared to those
        in the lowest quartile. These findings are particularly concerning because BPA exposure in
        childhood may program metabolic pathways in ways that persist into adulthood.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Microplastics in Placenta and Meconium
      </h3>
      <p>
        Research has now confirmed that microplastic exposure begins before birth. Studies have
        detected microplastic particles in human placental tissue, demonstrating that these particles
        can cross the placental barrier from mother to fetus. The implications are profound:
        developing fetuses are exposed to microplastics during the most critical period of organ
        formation. For more on this topic, see our detailed article on{' '}
        <Link href="/blog/microplastics-in-pregnancy" className="text-[#1E6B47] hover:underline font-medium">
          microplastics in pregnancy
        </Link>.
      </p>
      <p>
        Meconium -- the first stool a newborn produces -- has also been found to contain microplastics.
        Since meconium forms during fetal development, its microplastic content reflects prenatal
        exposure. The presence of plastic particles in meconium means that babies enter the world
        already carrying a microplastic burden inherited from their mother&apos;s exposure.
      </p>

      {/* Key Exposure Sources for Children */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Key Microplastic Exposure Sources for Children
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Baby Formula and Bottle Preparation
      </h3>
      <p>
        One of the most significant sources of microplastic exposure for infants is the preparation
        of baby formula in polypropylene (PP) bottles. A 2020 study published in <em>Nature Food</em>{' '}
        found that standard formula preparation -- which involves shaking hot water in a plastic
        bottle -- can release up to <strong>16.2 million microplastic particles per liter</strong>.
        The combination of heat and mechanical agitation dramatically increases particle release from
        the bottle walls.
      </p>
      <p>
        The researchers estimated that formula-fed infants in developed countries are exposed to an
        average of <strong>1.58 million microplastic particles per day</strong> during the first
        twelve months of life. This staggering number can be substantially reduced by preparing
        formula in glass containers and transferring it to the bottle after it has cooled.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Plastic Toys and Teething Items
      </h3>
      <p>
        Children spend hours chewing on, sucking, and mouthing plastic toys and teething products.
        Studies have found that plastic toys can release microplastic particles when subjected to
        simulated chewing conditions. Soft, flexible plastics (like those used in teething rings and
        rubber ducks) tend to release more particles than harder plastics. Heat and saliva can
        accelerate the breakdown of plastic surfaces, increasing particle release.
      </p>
      <p>
        Older or worn toys pose a greater risk than new ones, as surface degradation increases over
        time. Hand-me-down plastic toys, while economical and environmentally friendly in some
        respects, may shed more microplastics than newer products due to years of wear, UV exposure,
        and repeated washing.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Children&apos;s Clothing
      </h3>
      <p>
        The majority of children&apos;s clothing sold today is made from synthetic fabrics -- primarily
        polyester, nylon, and acrylic. These materials shed microplastic fibers with every wear and
        wash. A single load of polyester laundry can release up to <strong>700,000 microplastic
        fibers</strong>. Children wear their clothes close to the skin, and the fibers shed during
        wear can be inhaled or ingested through hand-to-mouth contact.
      </p>
      <p>
        School uniforms, athletic wear, and pajamas are especially likely to be made from synthetic
        materials. Choosing natural fiber clothing (cotton, wool, linen) where possible can reduce
        this exposure source. For a thorough overview, see our article on{' '}
        <Link href="/blog/microplastics-in-clothing" className="text-[#1E6B47] hover:underline font-medium">
          microplastics in clothing
        </Link>.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        School Lunch Packaging
      </h3>
      <p>
        School cafeterias routinely serve food in plastic trays, plastic-wrapped sandwiches, plastic
        cups, and single-use plastic utensils. Packaged snacks, juice boxes with plastic straws, and
        individually wrapped items all contribute to children&apos;s daily microplastic intake. A
        study on{' '}
        <Link href="/blog/microplastics-in-fast-food" className="text-[#1E6B47] hover:underline font-medium">
          microplastics in fast food
        </Link>{' '}
        found elevated microplastic levels in food served in plastic containers, and school lunch
        environments share many of the same packaging practices.
      </p>
      <p>
        Children who bring lunch from home in reusable stainless steel or glass containers are likely
        exposed to fewer food-contact microplastics than those who eat cafeteria food served on
        plastic trays with plastic utensils daily.
      </p>

      {/* What Parents Can Do by Age */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What Parents Can Do: Protection Strategies by Age
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Infants (0-12 Months)
      </h3>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Use glass baby bottles</strong> or stainless steel bottles instead of plastic.
          If you must use plastic bottles, prepare formula in a glass container first and transfer
          it after cooling -- never shake hot water in a plastic bottle.
        </li>
        <li>
          <strong>Breastfeed when possible.</strong> While breast milk can contain trace microplastics
          from the mother&apos;s exposure, the levels are far lower than those found in formula
          prepared in plastic bottles. Breastfeeding remains the safest feeding option from a
          microplastic perspective.
        </li>
        <li>
          <strong>Choose natural rubber or silicone teethers</strong> over plastic ones. Natural
          rubber (latex) and food-grade silicone are more stable materials that shed fewer particles
          than conventional plastic teething toys.
        </li>
        <li>
          <strong>Wet-mop floors regularly.</strong> Crawling infants are in constant contact with
          floor dust containing microplastic fibers. Frequent wet mopping removes dust more
          effectively than dry sweeping, which can redistribute particles into the air.
        </li>
        <li>
          <strong>Wash new baby clothes before first use</strong> and choose cotton or organic cotton
          when possible. New synthetic clothing sheds the most fibers in its first few washes.
        </li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Toddlers (1-3 Years)
      </h3>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Replace plastic plates, cups, and utensils</strong> with stainless steel, bamboo,
          or silicone alternatives. Toddlers eat multiple meals and snacks daily, and every meal on
          plastic is an exposure event.
        </li>
        <li>
          <strong>Limit plastic toy chewing.</strong> While you cannot prevent all mouthing behavior,
          choosing wooden, cloth, or natural rubber toys for toddlers who still mouth objects reduces
          direct plastic ingestion.
        </li>
        <li>
          <strong>Avoid heating food in plastic containers or microwave-safe plastic.</strong>{' '}
          Transfer food to glass or ceramic dishes before microwaving. Heat dramatically increases
          microplastic release from plastic containers.
        </li>
        <li>
          <strong>Filter drinking water.</strong> Use a high-quality water filter and serve water in
          stainless steel sippy cups or glass cups with silicone sleeves for grip.
        </li>
        <li>
          <strong>Vacuum with a HEPA filter</strong> regularly to reduce microplastic-laden dust in
          play areas.
        </li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        School-Age Children (4-12 Years)
      </h3>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Pack lunches in stainless steel or glass containers.</strong> Reusable bento-style
          lunch boxes made from stainless steel eliminate daily plastic food contact at school.
        </li>
        <li>
          <strong>Send a reusable water bottle</strong> made from stainless steel or glass instead of
          relying on disposable plastic bottles or school water fountains with plastic components.
        </li>
        <li>
          <strong>Choose natural fiber clothing</strong> for everyday wear. Cotton t-shirts, denim
          jeans, and wool sweaters shed minimal microplastic fibers compared to polyester and nylon.
        </li>
        <li>
          <strong>Teach children about microplastics.</strong> Age-appropriate education helps children
          understand why your family makes certain choices and empowers them to make informed decisions
          on their own.
        </li>
        <li>
          <strong>Reduce processed and packaged snacks.</strong> Fresh fruit, nuts, homemade treats,
          and snacks stored in reusable containers reduce both plastic packaging contact and the
          microplastics found in heavily processed foods.
        </li>
      </ul>

      {/* School Advocacy */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Advocating for Change at Your Child&apos;s School
      </h2>
      <p>
        Individual changes at home are important, but children spend a significant portion of their
        waking hours at school. Advocating for institutional changes can have a much larger impact
        than household changes alone.
      </p>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Request reusable dishware in the cafeteria.</strong> Many schools have switched from
          disposable plastic trays and utensils to reusable stainless steel or ceramic options. The
          upfront cost is offset by reduced ongoing purchasing of disposables. Present the
          microplastic health data to school administrators and PTA members.
        </li>
        <li>
          <strong>Advocate for water bottle filling stations.</strong> Modern filtered water bottle
          filling stations encourage children to use reusable bottles and reduce reliance on plastic
          cups and disposable bottles. Many schools have adopted these as part of sustainability
          initiatives.
        </li>
        <li>
          <strong>Support plastic-free lunch programs.</strong> Some school districts have begun
          piloting programs that reduce single-use plastic in food service. These initiatives reduce
          microplastic exposure for every child in the school, not just those whose parents are
          aware of the issue.
        </li>
        <li>
          <strong>Push for natural fiber options in school uniforms.</strong> If your child&apos;s
          school requires uniforms, advocate for cotton-blend options over all-polyester garments.
          Even a shift from 100% polyester to a cotton-polyester blend can meaningfully reduce
          microfiber shedding.
        </li>
        <li>
          <strong>Request HEPA air filtration in classrooms.</strong> HEPA filters can capture
          airborne microplastic fibers, reducing respiratory exposure for all students. This is
          especially important in older buildings with carpet flooring, which is a significant source
          of indoor microplastic dust.
        </li>
      </ul>

      {/* The Bigger Picture */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Bigger Picture: Why This Matters for Future Generations
      </h2>
      <p>
        The microplastic exposure that today&apos;s children experience is unprecedented. Global
        plastic production has increased from <strong>2 million tons in 1950 to over 400 million tons
        per year</strong> today, and it continues to grow. The children being born now will accumulate
        microplastics in their bodies for their entire lives at levels no previous generation has
        experienced.
      </p>
      <p>
        While the full health consequences of this lifetime exposure are still being studied, the
        precautionary principle demands action now. We do not need to wait for definitive proof of
        harm before taking reasonable steps to reduce children&apos;s exposure. The strategies
        outlined in this article are practical, affordable, and consistent with good health practices
        regardless of their specific effect on microplastics.
      </p>
      <p>
        Parents who take these steps are not just protecting their own children -- they are building
        awareness and demand for systemic changes in food packaging, school infrastructure, and product
        design that will benefit all children. Every family that switches to glass bottles, packs a
        plastic-free lunch, or advocates for change at their school is contributing to a larger shift
        that will reduce microplastic exposure for the next generation. For a complete family
        protection strategy, see our guide on{' '}
        <Link href="/blog/protect-family-from-microplastics" className="text-[#1E6B47] hover:underline font-medium">
          protecting your family from microplastics
        </Link>.
      </p>

      {/* FAQ Section */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Frequently Asked Questions
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        How many microplastics do children ingest compared to adults?
      </h3>
      <p>
        Studies have found that infants have 10 to 20 times more PET microplastic particles in their
        stool than adults. On a per-body-weight basis, children consume more food and water and breathe
        more air than adults, resulting in proportionally higher microplastic intake. Crawling infants
        may ingest up to 100,000 microplastic fibers per day from floor dust alone.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Are plastic baby bottles safe?
      </h3>
      <p>
        While plastic baby bottles meet safety standards for chemical leaching, research has shown
        that preparing formula in polypropylene bottles releases up to 16.2 million microplastic
        particles per liter, especially when hot water is used. Glass or stainless steel bottles
        are safer alternatives. If using plastic bottles, prepare formula in glass first and transfer
        after cooling.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Can microplastics affect my child&apos;s development?
      </h3>
      <p>
        Research is ongoing, but there are legitimate concerns. Chemicals associated with microplastics
        (phthalates, BPA, flame retardants) are known endocrine disruptors that can interfere with
        hormonal development. Studies have linked childhood BPA exposure to increased obesity risk,
        behavioral changes, and early puberty. The direct effects of the plastic particles themselves
        on child development are still being studied. For a comprehensive look at health impacts, see
        our article on{' '}
        <Link href="/blog/microplastics-health-effects" className="text-[#1E6B47] hover:underline font-medium">
          microplastic health effects
        </Link>.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Is breastfeeding safer than formula from a microplastic standpoint?
      </h3>
      <p>
        Yes, from a microplastic exposure perspective, breastfeeding results in significantly lower
        microplastic intake for the infant. While trace microplastics have been detected in breast
        milk, the levels are far lower than the millions of particles released when formula is prepared
        in plastic bottles with hot water. Breastfeeding is recommended as the safer option when
        possible, though mothers should not feel guilt if formula feeding is necessary -- the key is
        using glass bottles for preparation.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        What is the most important change a parent can make?
      </h3>
      <p>
        The single highest-impact change is switching from plastic baby bottles to glass or stainless
        steel for formula preparation. This alone can eliminate exposure to millions of microplastic
        particles per day. For older children, the most impactful change is filtering drinking water
        and using non-plastic food containers for school lunches and daily meals.
      </p>

      {/* App CTA */}
      <div className="bg-gradient-to-r from-[#1E6B47]/20 to-[#1E6B47]/5 rounded-xl p-6 mt-8">
        <h3 className="text-xl font-bold text-gray-800 mb-2">Check Your Products with the MicroPlastics App</h3>
        <p className="text-gray-600 mb-4">Scan any product to instantly see its microplastic risk level. Start with 3 free scans.</p>
        <AppStoreLink source="blog_post_inline" className="inline-flex items-center gap-2 bg-[#1E6B47] text-white font-semibold px-6 py-3 rounded-control hover:bg-[#f0a060] transition-colors pressable">Download Free on iOS</AppStoreLink>
      </div>

      {/* Related Articles */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Related Articles
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <Link href="/blog/microplastics-in-baby-products" className="text-[#1E6B47] hover:underline font-medium">
            Microplastics in Baby Products: What Parents Need to Know
          </Link>
        </li>
        <li>
          <Link href="/blog/microplastics-in-pregnancy" className="text-[#1E6B47] hover:underline font-medium">
            Microplastics in Pregnancy: Risks and How to Reduce Exposure
          </Link>
        </li>
        <li>
          <Link href="/blog/protect-family-from-microplastics" className="text-[#1E6B47] hover:underline font-medium">
            How to Protect Your Family From Microplastics
          </Link>
        </li>
        <li>
          <Link href="/blog/microplastics-health-effects" className="text-[#1E6B47] hover:underline font-medium">
            The Health Effects of Microplastics: What Science Says
          </Link>
        </li>
      </ul>

      <BlogWhatTheAppChecks
        items={[
          <>Baby/kid product material, glass, stainless, silicone, polypropylene, PPSU.</>,
          <>Packaging type, jar vs pouch vs multi-layer plastic.</>,
          <>Brand and product line, clean certifications flagged.</>,
          <>Use-context flags you log, sterilization heat, dishwasher cycles, age.</>,
          <>Cited published research behind each 0–100 score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan baby gear and pregnancy products before buying"
        sub="Bottles, sippy cups, baby food pouches, cosmetics. The app weighs material + brand + condition and suggests cleaner-packaged alternatives."
        buttonLabel="Scan baby gear in the app"
      />


      <BlogFAQ
        items={[
          {
            question: 'Do children have more microplastics in their bodies than adults?',
            answer:
              'Yes. A 2021 study by Zhang et al. found infant feces contained roughly 10x more microplastic particles per gram than adult feces. The drivers are plastic baby bottles, plastic toys (often mouthed by infants), packaged baby foods, and floor-level dust exposure.',
          },
          {
            question: 'What is the biggest microplastic source for babies?',
            answer:
              'Polypropylene baby bottles. A 2020 Nature Food study found that preparing formula in PP bottles released 1-16 million microplastic particles per liter. Glass, medical-grade silicone, or stainless steel bottles eliminate this source. The formula prep (hot water + plastic) is the worst step.',
          },
          {
            question: 'What is the safest baby bottle material for microplastics?',
            answer:
              'Borosilicate glass is the safest, chemically inert, releases zero microplastics at any temperature. Medical-grade silicone and stainless steel are excellent alternatives, especially for travel. PPSU is acceptable as a step up from PP. Polycarbonate (even BPA-free) is the worst.',
          },
          {
            question: 'Do plastic toys release microplastics?',
            answer:
              'Yes: especially when chewed, scratched, exposed to heat, or aged. Soft PVC toys are worst (also leach phthalates). Black recycled-plastic toys can contain brominated flame retardants. Wood, silicone, and natural rubber are cleaner alternatives.',
          },
          {
            question: 'What should I prioritize first to reduce my child\'s microplastic exposure?',
            answer:
              'In order of impact: (1) feeding system (glass/silicone bottles and glass-jar baby food, not pouches; (2) drinking water) filtered, in glass or stainless; (3) indoor air (HEPA filter, hard floors over synthetic carpet; (4) sleep surface) natural-fibre mattress and bedding; (5) replace plastic dishes/sippy cups with stainless and silicone.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Prospective cohort study (1,750 mother-infant pairs)',
            year: 2026,
            title: 'Impact of placental microplastics on birth anthropometrics: A cross-sectional study',
            publisher: 'Ecotoxicology and Environmental Safety',
            url: 'https://www.sciencedirect.com/science/article/pii/S0147651325019177',
          },
          {
            authors: 'Yakovenko N, et al.',
            year: 2025,
            title: 'Human exposure to PM10 microplastics in indoor air',
            publisher: 'PLOS One',
            url: 'https://journals.plos.org/plosone/article?id=10.1371%2Fjournal.pone.0328011',
          },
          {
            authors: 'Garcia MA, Liu R, Nihart A, et al.',
            year: 2024,
            title: 'Quantitation and identification of microplastics in human placental specimens',
            publisher: 'Toxicological Sciences',
            url: 'https://academic.oup.com/toxsci/article/199/1/81/7609299',
          },
          {
            authors: 'Li D, Shi Y, Yang L, et al.',
            year: 2020,
            title: 'Microplastic release from polypropylene infant feeding bottles',
            publisher: 'Nature Food',
            url: 'https://www.nature.com/articles/s43016-020-00171-y',
          },
          {
            authors: 'Ragusa A, Notarstefano V, Svelato A, et al.',
            year: 2022,
            title: 'Raman Microspectroscopy Detection and Characterisation of Microplastics in Human Breastmilk',
            publisher: 'Polymers',
            url: 'https://www.mdpi.com/2073-4360/14/13/2700',
          },
          {
            authors: 'ACOG Committee Opinion 832',
            year: 2021,
            title: 'Reducing prenatal exposure to toxic environmental agents',
            publisher: 'American College of Obstetricians and Gynecologists',
            url: 'https://www.acog.org/clinical/clinical-guidance/committee-opinion/articles/2021/07/reducing-prenatal-exposure-to-toxic-environmental-agents',
          },
          {
            authors: 'Leslie HA, van Velzen MJM, Brandsma SH, et al.',
            year: 2022,
            title: 'Discovery and quantification of plastic particle pollution in human blood',
            publisher: 'Environment International',
            url: 'https://www.sciencedirect.com/science/article/pii/S0160412022001258',
          },
        ]}
      />


    </BlogPostLayout>
  )
}
